/**
 * Error types for API error handling.
 *
 * Defines structured error types for different error scenarios,
 * enabling type-safe error handling throughout the application.
 */

/**
 * Error codes for specific error scenarios.
 * These are used to identify error types for conditional handling.
 */
export type ApiErrorCode =
  | 'SEAT_LIMIT_EXCEEDED'
  | 'DUPLICATE_EMAIL'
  // ALREADY_EXISTS: creating a tenant whose org name or domain collides with an
  // existing org (409). Distinct from DUPLICATE_EMAIL, which is a user-email
  // collision on invite — the backend keeps them separate so the UI never labels
  // an org-name/domain conflict as an email conflict.
  | 'ALREADY_EXISTS'
  // CANNOT_MODIFY_VENDOR: a tenant-level mutation (delete) targeted the ModernPath
  // vendor org specifically (409). Its own code lets the UI give the vendor org a
  // bespoke message.
  | 'CANNOT_MODIFY_VENDOR'
  // CANNOT_MODIFY_PROTECTED: a tenant-level mutation (delete) targeted a protected
  // system org that is NOT the vendor org — the built-in ZITADEL admin org or the
  // instance default org (409). Kept distinct from CANNOT_MODIFY_VENDOR so the code
  // names the actual reason rather than overloading "vendor" onto every protected org.
  | 'CANNOT_MODIFY_PROTECTED'
  // CANNOT_MODIFY_SELF: the caller tried to change their own role (422). Distinct
  // from the self-delete guard (which the backend reports as CANNOT_REMOVE_SELF
  // without a dedicated code here) so the two self-guards stay addressable.
  | 'CANNOT_MODIFY_SELF'
  // USER_HAS_NO_ACCESS: a role change targeted a user with no platform grant in
  // the org — e.g. access was revoked (409). The role lives on the grant, so
  // there is nothing to update, and a toggle must not re-grant revoked access.
  | 'USER_HAS_NO_ACCESS'
  | 'USER_NOT_FOUND'
  | 'ORG_NOT_ENTITLED'
  | 'INVALID_REQUEST'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'INTERNAL_ERROR'
  | 'UPSTREAM_ERROR'
  | 'UNKNOWN';

/**
 * Base API error structure.
 *
 * Represents an error returned by the API with optional structured details.
 */
export interface ApiError {
  /** HTTP status code */
  status: number;

  /** Human-readable error message */
  message: string;

  /** Machine-readable error code for conditional handling */
  code?: ApiErrorCode;

  /**
   * Additional error details.
   * May contain field-level validation errors or other context.
   */
  details?: Record<string, unknown>;
}

/**
 * Error thrown when the organization has reached its seat limit.
 *
 * Occurs when trying to invite a new user but all seats are in use.
 */
export interface SeatLimitError extends ApiError {
  code: 'SEAT_LIMIT_EXCEEDED';
  details: {
    /** Current number of seats in use */
    currentSeats: number;
    /** Maximum seats allowed for the organization */
    maxSeats: number;
  };
}

/**
 * Error thrown when trying to invite a user with an email that already exists.
 */
export interface DuplicateEmailError extends ApiError {
  code: 'DUPLICATE_EMAIL';
  details: {
    /** The email address that already exists */
    email: string;
  };
}

/**
 * Error thrown when a requested user is not found.
 */
export interface UserNotFoundError extends ApiError {
  code: 'USER_NOT_FOUND';
  details: {
    /** The user ID that was not found */
    userId: string;
  };
}

/**
 * Error thrown when the active organization is not entitled to manage platform
 * team members (e.g. the owning/vendor org rather than a customer org).
 *
 * Returned by the invite endpoint as HTTP 422 (deliberately distinct from the
 * 403 "lacks platform.user.admin" case so the two don't blur). Consumers key
 * off `code`, not the status. Entitlement is decided by the backend — the SPA
 * does not infer org ownership from the claim. No `details` payload.
 */
export interface OrgNotEntitledError extends ApiError {
  code: 'ORG_NOT_ENTITLED';
}

/**
 * Error thrown when creating a tenant whose org name or domain collides with an
 * existing org (HTTP 409). Deliberately distinct from DuplicateEmailError (a
 * user-email collision on invite) so an org conflict is never mislabeled as an
 * email conflict. Consumers key off `code`, not the status. No `details` payload.
 */
export interface AlreadyExistsError extends ApiError {
  code: 'ALREADY_EXISTS';
}

/**
 * Error thrown when a tenant-level mutation (delete) targets the ModernPath vendor
 * org specifically (HTTP 409). Consumers key off `code`. No `details` payload.
 */
export interface CannotModifyVendorError extends ApiError {
  code: 'CANNOT_MODIFY_VENDOR';
}

/**
 * Error thrown when a tenant-level mutation (delete) targets a protected system org
 * that is NOT the vendor org — the built-in ZITADEL admin org or the instance
 * default org (HTTP 409). Consumers key off `code`. No `details` payload.
 */
export interface CannotModifyProtectedError extends ApiError {
  code: 'CANNOT_MODIFY_PROTECTED';
}

/**
 * Type guard to check if an error is an ApiError.
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'message' in error &&
    typeof (error as ApiError).status === 'number' &&
    typeof (error as ApiError).message === 'string'
  );
}

/**
 * Type guard to check if an error is a SeatLimitError.
 */
export function isSeatLimitError(error: unknown): error is SeatLimitError {
  return isApiError(error) && error.code === 'SEAT_LIMIT_EXCEEDED';
}

/**
 * Type guard to check if an error is a DuplicateEmailError.
 */
export function isDuplicateEmailError(error: unknown): error is DuplicateEmailError {
  return isApiError(error) && error.code === 'DUPLICATE_EMAIL';
}

/**
 * Type guard to check if an error is a UserNotFoundError.
 */
export function isUserNotFoundError(error: unknown): error is UserNotFoundError {
  return isApiError(error) && error.code === 'USER_NOT_FOUND';
}

/**
 * Type guard to check if an error is an OrgNotEntitledError.
 */
export function isOrgNotEntitledError(error: unknown): error is OrgNotEntitledError {
  return isApiError(error) && error.code === 'ORG_NOT_ENTITLED';
}

/**
 * Type guard to check if an error is an AlreadyExistsError.
 */
export function isAlreadyExistsError(error: unknown): error is AlreadyExistsError {
  return isApiError(error) && error.code === 'ALREADY_EXISTS';
}

/**
 * Type guard to check if an error is a CannotModifyVendorError.
 */
export function isCannotModifyVendorError(error: unknown): error is CannotModifyVendorError {
  return isApiError(error) && error.code === 'CANNOT_MODIFY_VENDOR';
}

/**
 * Type guard to check if an error is a CannotModifyProtectedError (a protected
 * system org that is not the vendor org).
 */
export function isCannotModifyProtectedError(error: unknown): error is CannotModifyProtectedError {
  return isApiError(error) && error.code === 'CANNOT_MODIFY_PROTECTED';
}

/**
 * Creates an ApiError from an HTTP response.
 *
 * @param status - HTTP status code
 * @param message - Error message
 * @param code - Optional error code
 * @param details - Optional error details
 */
export function createApiError(
  status: number,
  message: string,
  code?: ApiErrorCode,
  details?: Record<string, unknown>
): ApiError {
  return {
    status,
    message,
    ...(code && { code }),
    ...(details && { details }),
  };
}

/**
 * Default error messages for common HTTP status codes.
 */
export const DEFAULT_ERROR_MESSAGES: Record<number, string> = {
  400: 'Invalid request',
  401: 'Please sign in to continue',
  403: 'You do not have permission to perform this action',
  404: 'The requested resource was not found',
  409: 'A conflict occurred with the current state',
  500: 'An internal error occurred',
  502: 'Unable to reach the server',
  503: 'Service temporarily unavailable',
};
